import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useRef, useState } from "react";
import { Popover } from "react-aria-components";
import { MDSSidebarWidths } from "@/mds/foundations";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSHoverContent,
  MDSHoverContentPlacement,
  MDSHoverContentGroup,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { SidebarDropdownMenu } from "@/components/layout/components/sidebar/sidebar-dropdown-menu";
import { useMatch } from "react-router";
import { appRoutes } from "@/app/router";
import { DebugModeTapDetector } from "../../account-profile/debug-mode-tap-detector";
import {
  StyledAccountInfoWrapper,
  StyledSidebarWrapper,
} from "@/components/layout/components/sidebar/sidebar.styles";
import { EventContext } from "@/domains/metrics/context";
import { SidebarContentsRecentCollectionsList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-recent-collections";
import { SidebarContentsPinnedItemsList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-pinned-items";
import { SidebarContentsRecentNotesList } from "@/components/layout/components/sidebar/sidebar-contents/sidebar-contents-recent-notes";
import { NoteObservable } from "@/store/note/NoteObservable";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { isMac } from "@/domains/platform/isMac";
import { clientEnvModule } from "@/modules/client-env";
import { AccountProfileImage } from "@/components/layout/components/account-profile/account-profile-image";
import { OnboardingTooltip } from "@/components/note/floating-actions/onboarding-tooltip";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";
import { DesktopUpdateNotice } from "@/app/core/DesktopUpdateNotice";

interface SidebarCollapsedProps {
  setIsClassicChatModalOpen: (b: boolean) => void;
  setIsImportModalOpen: (b: boolean) => void;
  toggleSidebar: () => void;
  shouldHideSidebarToggle: boolean;
}

export const SidebarCollapsed = observer(
  ({
    setIsClassicChatModalOpen,
    setIsImportModalOpen,
    toggleSidebar,
    shouldHideSidebarToggle,
  }: SidebarCollapsedProps) => {
    const { store } = useAppStore();
    const isMacOS = isMac();
    const isDesktop = clientEnvModule.isDesktop();

    const isNotesPath = useMatch(appRoutes.notesList({}));
    const isCollectionsPath = useMatch(appRoutes.collections({}));

    const isTouchDevice = store.interface.isTouchDevice;
    const { profileDisplayName, profilePhotoUrl } = store.account.myAccount;

    const hoverGroup = useMemo(() => new MDSHoverContentGroup(), []);
    const closeAll = useCallback(() => hoverGroup.hideAll({}), [hoverGroup]);

    // Dropdown menu hover state — uses onMouseEnter/onMouseLeave with a
    // timeout instead of MDSHoverContent (floating-ui) so that React Aria
    // portaled submenus (e.g. the theme switcher) don't cause premature dismiss.
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownTriggerRef = useRef<HTMLDivElement>(null);
    const dropdownHideTimeout = useRef<ReturnType<typeof setTimeout>>();

    const showDropdown = useCallback(() => {
      hoverGroup.hideAll({});
      clearTimeout(dropdownHideTimeout.current);
      setIsDropdownOpen(true);
    }, [hoverGroup]);

    const hideDropdown = useCallback(() => {
      dropdownHideTimeout.current = setTimeout(() => setIsDropdownOpen(false), 150);
    }, []);

    const cancelDropdownHide = useCallback(() => {
      clearTimeout(dropdownHideTimeout.current);
    }, []);

    const hasPinnedItems = store.favoriteItems.sortedFavoriteItems.length > 0;
    const hasRecentNotes =
      store.recentItems.sortedRecentItems.filter(item => item instanceof NoteObservable).length > 0;
    const hasRecentCollections =
      store.recentItems.sortedRecentItems.filter(item => item instanceof CollectionObservable)
        .length > 0;

    const toggleButtonRef = useRef<HTMLButtonElement>(null);
    const shouldShowOnboardingTooltip = store.gettingStarted.isCollapsedSidebarTooltipVisible;

    return (
      <StyledSidebarCollapsedWrapper $avoidMacOsTrafficLights={isMacOS && isDesktop}>
        <StyledAccountInfoWrapper
          ref={dropdownTriggerRef}
          data-test-id="sidebar-account-trigger"
          onMouseEnter={showDropdown}
          onMouseLeave={hideDropdown}
        >
          <DebugModeTapDetector>
            <AccountProfileImage
              size={24}
              photoUrl={profilePhotoUrl}
              displayName={profileDisplayName}
              showSyncStatus
            />
          </DebugModeTapDetector>
        </StyledAccountInfoWrapper>
        {isDropdownOpen && (
          <Popover
            triggerRef={dropdownTriggerRef}
            isOpen={isDropdownOpen}
            isNonModal
            placement="right top"
            offset={10}
          >
            <StyledDropdownMenuContainer
              onMouseEnter={cancelDropdownHide}
              onMouseLeave={hideDropdown}
            >
              <SidebarDropdownMenu
                displayAccountInfo
                setIsClassicChatModalOpen={setIsClassicChatModalOpen}
                setIsImportModalOpen={setIsImportModalOpen}
                onClick={() => {
                  closeAll();
                  setIsDropdownOpen(false);
                }}
              />
            </StyledDropdownMenuContainer>
          </Popover>
        )}

        <MDSTooltip
          config={getShortcutTooltipConfig({
            action: "create_note",
            label: "Create note",
            placement: MDSTooltipPlacement.Top,
          })}
        >
          <MDSIconButton
            size={MDSIconButtonSize.Medium}
            iconKind={MDSIconKind.ComposeInline}
            variant={MDSIconButtonVariant.Chrome}
            onClick={() => store.notes.composeNewNote({ eventContext: EventContext.GlobalActions })}
            rounded
          />
        </MDSTooltip>

        <StyledSidebarContentContainer>
          <MDSHoverContent
            group={hoverGroup}
            content={() =>
              hasPinnedItems ? (
                <StyledHoverContentContainer>
                  <SidebarContentsPinnedItemsList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MDSHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MDSIconKind.Thumbtack}
              variant={MDSIconButtonVariant.OnChrome}
              isDisabled={!hasPinnedItems}
            />
          </MDSHoverContent>
          <MDSHoverContent
            isTouchDevice={isTouchDevice}
            group={hoverGroup}
            content={() =>
              hasRecentNotes ? (
                <StyledHoverContentContainer>
                  <SidebarContentsRecentNotesList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MDSHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MDSIconKind.Notebook}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={event => store.navigation.goToNotes({ event })}
              $isActive={!!isNotesPath}
            />
          </MDSHoverContent>
          <MDSHoverContent
            isTouchDevice={isTouchDevice}
            group={hoverGroup}
            content={() =>
              hasRecentCollections ? (
                <StyledHoverContentContainer>
                  <SidebarContentsRecentCollectionsList includeTitle onClick={closeAll} />
                </StyledHoverContentContainer>
              ) : null
            }
            placement={MDSHoverContentPlacement.Right}
          >
            <StyledIconButton
              iconKind={MDSIconKind.Collection}
              variant={MDSIconButtonVariant.OnChrome}
              onClick={event => store.navigation.goToCollections({ event })}
              $isActive={!!isCollectionsPath}
            />
          </MDSHoverContent>
        </StyledSidebarContentContainer>
        <StyledDesktopUpdateNoticeSlot $reserveToggleSpace={!shouldHideSidebarToggle}>
          <DesktopUpdateNotice presentation="collapsed-sidebar" />
        </StyledDesktopUpdateNoticeSlot>
        {!shouldHideSidebarToggle && (
          <MDSTooltip
            config={getShortcutTooltipConfig({
              action: "toggle_sidebar",
              label: "Toggle sidebar",
              placement: MDSTooltipPlacement.Top,
            })}
          >
            <StyledSidebarToggleButton
              ref={toggleButtonRef}
              size={MDSIconButtonSize.Medium}
              iconKind={MDSIconKind.ArrowRightToLine}
              variant={MDSIconButtonVariant.Transparent}
              onClick={toggleSidebar}
            />
          </MDSTooltip>
        )}
        {shouldShowOnboardingTooltip && (
          <OnboardingTooltip
            title="New to Mem?"
            subtitle="Expand your window to see your Getting Started guide in the sidebar"
            triggerRef={toggleButtonRef}
            isOpen={shouldShowOnboardingTooltip}
            onDismiss={store.gettingStarted.dismissOnboardingTooltip}
            placement="right bottom"
            offset={24}
            pointCorner="bottom-left"
            width={218}
          />
        )}
      </StyledSidebarCollapsedWrapper>
    );
  }
);

const StyledSidebarCollapsedWrapper = styled(StyledSidebarWrapper)`
  height: 100%;
  align-items: center;
  width: ${MDSSidebarWidths.collapsed}px;
  flex: 1;
  min-height: 0;
`;
StyledSidebarCollapsedWrapper.displayName = "StyledSidebarCollapsedWrapper";

const StyledSidebarContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
`;
StyledSidebarContentContainer.displayName = "StyledSidebarContentContainer";

const StyledDesktopUpdateNoticeSlot = styled.div<{ $reserveToggleSpace: boolean }>`
  display: flex;
  margin-top: auto;
  margin-bottom: ${({ $reserveToggleSpace, theme }) =>
    $reserveToggleSpace
      ? `calc(${theme.spacing["10"]} + ${theme.spacing["2"]})`
      : theme.spacing["2"]};
`;
StyledDesktopUpdateNoticeSlot.displayName = "StyledDesktopUpdateNoticeSlot";

const StyledIconButton = styled(MDSIconButton)<{ $isActive?: boolean }>`
  background-color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.colors.background.button.onChrome.hover
      : theme.colors.background.button.onChrome.default};
`;
StyledIconButton.displayName = "StyledIconButton";

const StyledSidebarToggleButton = styled(MDSIconButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.button.navigation.default};
  border-radius: ${({ theme }) => theme.borderRadius.none};
  border-top-left-radius: ${({ theme }) => theme.borderRadius.xlarge};
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.xlarge};

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ theme }) => theme.colors.background.button.navigation.hover};
  }
`;
StyledSidebarToggleButton.displayName = "StyledSidebarToggleButton";

const StyledDropdownMenuContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.surface.menu};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`;
StyledDropdownMenuContainer.displayName = "StyledDropdownMenuContainer";

const StyledHoverContentContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing["4"]};
  padding-inline: ${({ theme }) => theme.spacing["3"]};
  background-color: ${({ theme }) => theme.colors.surface.menu};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 288px;
`;
StyledHoverContentContainer.displayName = "StyledHoverContentContainer";
